import { Client, Lesson, Slot } from '@opanuj/shared/types';
import { Database, get, ref, update } from 'firebase/database';

type ProductDeleteOptions = {
	clientId: string;
	slotId: string;
	clientUid?: string;
};

const values = <T>(value: T[] | Record<string, T> | null | undefined): T[] => {
	if (Array.isArray(value)) return value;
	return Object.values(value ?? {});
};

const belongsToClient = (student: { clientId?: string; uid?: string }, clientId: string, clientUid?: string) => {
	if (student.clientId) return student.clientId === clientId;
	return Boolean(clientUid && student.uid === clientUid);
};

export const productDelete = async (
	database: Database,
	{ clientId, slotId, clientUid }: ProductDeleteOptions,
): Promise<void> => {
	if (!clientId || !slotId) return;

	const [clientSnapshot, slotSnapshot] = await Promise.all([
		get(ref(database, `clients/${clientId}`)),
		get(ref(database, `slots/${slotId}`)),
	]);
	const client = clientSnapshot.val() as Client | null;
	const slot = slotSnapshot.val() as (Slot & { lessons?: Lesson[] | Record<string, Lesson> }) | null;
	if (!client || !slot) return;

	const resolvedClientUid = clientUid ?? client.uid;
	const products = Object.values(client.products!);
	const productId = Object.keys(client.products ?? {}).find((key) => client.products![key].slotId === slotId);
	const studentsToRemove = products
		.filter((product) => product.slotId === slotId)
		.map((product) => product.student)
		.filter((student) => belongsToClient(student, clientId, resolvedClientUid));
	if (!studentsToRemove.length) return;

	const lessons = values(slot.lessons);
	const updates: Record<string, unknown> = {
		[`clients/${clientId}/products/${productId}`]: null,
	};
	const remainingLessons: Lesson[] = [];

	for (const lesson of lessons) {
		const remainingStudents = lesson.students.filter((student) => {
			const belongsToProduct = studentsToRemove.some((productStudent) => {
				if (productStudent.clientId) return student.clientId === productStudent.clientId;
				return Boolean(productStudent.uid && student.uid === productStudent.uid);
			});
			return !belongsToProduct;
		});

		if (!remainingStudents.length) {
			updates[`lessons/${lesson.id}`] = null;
			continue;
		}

		const updatedLesson = { ...lesson, students: remainingStudents };
		remainingLessons.push(updatedLesson);
		updates[`lessons/${lesson.id}/students`] = remainingStudents;
	}

	updates[`slots/${slotId}/lessons`] = remainingLessons;
	if (!remainingLessons.length) {
		updates[`slots/${slotId}/started`] = false;
	}

	await update(ref(database), updates);
};